<template>
  <TopBarShell
    class="guest-top-bar pt-2 pb-2 sm:pb-3 px-2 sm:px-0"
    :class="compactDesktopTopSpacing ? 'sm:pt-0' : 'sm:pt-8'"
    data-testid="guest-top-bar"
    :pad-actions-on-mobile="padActionsOnMobile"
  >
    <template #actions>
      <!-- Mode switcher (same-org logged-in viewers only). Gated on the
           divider so an empty chip doesn't orphan a separator. -->
      <template v-if="isSameOrgAsItem">
        <ViewerModeToggle />
        <span v-if="hasMultipleModes" class="guest-top-bar__separator" />
      </template>

      <OcrSelectionToolbar />

      <button class="guest-top-bar__icon-btn" title="Copy link" aria-label="Copy link" data-testid="guest-topbar-copy" @click.prevent="onCopy">
        <Icon name="general.copy" size="base" />
      </button>

      <button
        v-if="downloadEnabled"
        class="guest-top-bar__icon-btn"
        title="Download"
        aria-label="Download"
        data-testid="guest-topbar-download"
        @click.prevent="downloadAction"
      >
        <Icon name="general.download" size="base" />
      </button>

      <button class="guest-top-bar__icon-btn" title="Share" aria-label="Share" data-testid="guest-topbar-share" @click.prevent="openShareModalAction">
        <Icon :name="getSharingIcon()" size="base" />
      </button>

      <!-- Sign Up CTA (anonymous users, hidden on white-label / custom domains) -->
      <Button v-if="!isLoggedIn && !hideBranding && !isCustomDomain" type="solid" variant="blue" data-testid="guest-topbar-signup" @click.prevent="onSignUp">
        <span class="hidden sm:inline">Get Zight for Free</span>
        <span class="sm:hidden">Sign Up</span>
      </Button>

      <!-- Sign In fallback for white-label guests so they aren't stranded
           when the "Get Zight" CTA is suppressed. Pre-SW-6382 they got this
           from HeaderGuest unconditionally; here it only appears when the
           Sign Up CTA above can't render. -->
      <Button
        v-else-if="!isLoggedIn && !isCustomDomain"
        type="link"
        variant="black"
        icon="general.signIn"
        data-testid="guest-topbar-signin"
        @click.prevent="onSignIn"
      >
        Sign In
      </Button>

      <Button
        v-else-if="isLoggedIn"
        type="solid"
        variant="blue"
        icon="general.logoBW"
        :loading="isCopying"
        :disabled="isCopying"
        data-testid="guest-topbar-save"
        @click.prevent="saveAction"
      >
        <span class="hidden sm:inline">Save to Zight</span>
        <span class="sm:hidden">Save</span>
      </Button>
    </template>
  </TopBarShell>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon, Button } from '@/storybook';

import TopBarShell from './TopBarShell.vue';
import OcrSelectionToolbar from './OcrSelectionToolbar.vue';
import ViewerModeToggle from '@/modules/viewer/components/ViewerModeToggle.vue';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useUserStore } from '@/store/useUserStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useItemShare } from '@/modules/viewer/composables/useItemShare';
import { useAuthModals } from '@/composables/useAuthModals';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';

defineProps<{
  padActionsOnMobile?: boolean;
  compactDesktopTopSpacing?: boolean;
}>();

const viewerItemStore = useViewerItemStore();
const { isSameOrgAsItem } = useUser();

const { availableModes } = useViewerAvailableModes();
const hasMultipleModes = computed(() => availableModes.value.length > 1);

const { downloadEnabled, hideBranding, isCopying } = storeToRefs(viewerItemStore);
const { isLoggedIn } = storeToRefs(useUserStore());
const { isCustomDomain } = storeToRefs(useSettingsStore());

const { copyAction, downloadAction, openShareModalAction, saveAction } = useItemActions();
const { getSharingIcon } = useItemShare();
const { showSignInModal, showSignUpModal } = useAuthModals();

const onCopy = () => {
  copyAction('viewer_toolbar');
};

const onSignUp = () => {
  showSignUpModal({
    signedUpFrom: 'viewer_guest_topbar',
  });
};

const onSignIn = () => {
  showSignInModal();
};
</script>

<style scoped>
.guest-top-bar__icon-btn {
  @apply p-2 rounded-md text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.guest-top-bar__separator {
  @apply w-px h-5 bg-zinc-300 dark:bg-zinc-600 mx-1;
}
</style>
